import {
  APP_TIMEZONE,
  atualizarBloqueioDeVeiculoSchema,
  criarBloqueioDePrestadorSchema,
  criarBloqueioDeVeiculoSchema,
  formatInTimeZone,
  MOTIVOS_CONTROLADOS_PELA_RESERVA,
  MOTIVOS_OPERACIONAIS,
  type MotivoDeBloqueio,
  periodoValidoSchema,
  ROTULO_DO_MOTIVO_DE_BLOQUEIO,
  ROTULO_DO_TIPO_DE_PRESTADOR,
  TAMANHO_MAXIMO_DE_PAGINA,
} from '@locaveri/shared'
import { LoaderCircle, RefreshCw } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { type FiltrosDeVeiculos, identificacaoDoVeiculo, useVeiculos } from '@/features/frota/api'
import { errorMessage } from '@/lib/api'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'

import {
  type BloqueioDeVeiculo,
  type FiltrosDePrestadores,
  useAtualizarBloqueio,
  useCriarBloqueio,
  useCriarBloqueioDePrestador,
  usePrestadores,
} from './cotacao'

/**
 * Bloqueio de ocupação — do carro (manutenção, limpeza, vistoria, sinistro) e
 * da pessoa (o turno do motorista ou do segurança na escala).
 *
 * ---------------------------------------------------------------------------
 * UM DIÁLOGO PARA OS DOIS ALVOS
 * ---------------------------------------------------------------------------
 * Motivo, início, fim e observação são exatamente os mesmos campos, com as
 * mesmas regras de período — `camposDoBloqueio` é literalmente compartilhado
 * pelos dois schemas de `@locaveri/shared`. Dois diálogos seriam duas cópias do
 * mesmo formulário, e a segunda é a que esqueceria de recusar `ALOCACAO`.
 * O que muda é UMA linha: qual entidade se ocupa.
 *
 * ---------------------------------------------------------------------------
 * `ALOCACAO` E `RESERVA` NÃO SE ESCOLHEM
 * ---------------------------------------------------------------------------
 * O seletor usa `MOTIVOS_OPERACIONAIS`, que é a lista SEM os dois motivos que
 * nascem da reserva (Fase 4). Criá-los à mão deixaria o carro ocupado sem
 * nenhuma reserva que explique por quê; liberá-los por aqui não cancelaria a
 * reserva — só faria o sistema vender de novo um carro que já tem dono. A API
 * recusa (422) e a tela sequer oferece o caminho: quando um bloqueio desses
 * chega em edição, o formulário fica só de leitura com o motivo escrito.
 *
 * ---------------------------------------------------------------------------
 * O VEÍCULO NÃO SE EDITA
 * ---------------------------------------------------------------------------
 * Mover o bloqueio para outro carro é liberar um e ocupar outro, e a constraint
 * de sobreposição do Postgres precisa avaliar os dois. Quem errou o carro apaga
 * e cria — é a mesma razão pela qual a escala não tem `PATCH` nenhum.
 *
 * ---------------------------------------------------------------------------
 * O CAMPO É HORA LOCAL; O FIO É INSTANTE COM FUSO
 * ---------------------------------------------------------------------------
 * `<input type="datetime-local">` devolve hora de parede sem fuso nenhum, e
 * `instanteSchema` recusa string sem deslocamento — com razão: `09:00` em São
 * Paulo e `09:00` em Manaus são três horas de diferença, meia diária. A
 * conversão mora aqui, exportada, porque o simulador e a tela de
 * disponibilidade fazem a MESMA pergunta sobre o mesmo intervalo; uma segunda
 * cópia seria a que divergiria no dia em que o país voltasse a ter horário de
 * verão.
 */

// ---------------------------------------------------------------------------
// Hora local ↔ instante
//
// As funções de PERÍODO da fase moram todas aqui — conversão, crítica e os
// valores sugeridos dos campos. O simulador e a tela de disponibilidade fazem a
// mesma pergunta sobre o mesmo intervalo; uma segunda cópia seria a que aceita o
// período que a outra recusa, ou a que erra o dia no turno da noite.
// ---------------------------------------------------------------------------

/** O formato que o `<input type="datetime-local">` produz. */
const CAMPO_DE_INSTANTE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/

const UM_DIA_EM_MS = 86_400_000

/**
 * O deslocamento do fuso da operação, resolvido pelo `Intl` do navegador.
 *
 * `longOffset` devolve `GMT-03:00`. Ele é consultado no instante em questão, e
 * não fixado em `-03:00`, porque o Brasil já teve horário de verão e pode voltar
 * a ter: uma constante escrita à mão erraria uma hora em quatro meses do ano, e
 * o sintoma seria uma diária a mais numa reserva de virada de dia.
 */
const FORMATADOR_DE_DESLOCAMENTO = new Intl.DateTimeFormat('en-US', {
  timeZone: APP_TIMEZONE,
  timeZoneName: 'longOffset',
})

function deslocamentoDaOperacao(instante: Date): string {
  const parte = FORMATADOR_DE_DESLOCAMENTO.formatToParts(instante).find(
    (item) => item.type === 'timeZoneName',
  )
  const bruto = (parte?.value ?? '').replace('GMT', '')

  // Fuso de deslocamento zero sai como `GMT` puro, sem número.
  return bruto.length === 0 ? '+00:00' : bruto
}

/**
 * `2026-03-15T09:00` (hora do ponto de retirada) → `2026-03-15T09:00:00-03:00`.
 *
 * `null` quando o campo ainda não está preenchido por inteiro — quem chama
 * transforma isso em erro de campo, nunca em uma requisição com data inventada.
 */
export function paraInstante(campo: string): string | null {
  const valor = campo.slice(0, 16)

  if (!CAMPO_DE_INSTANTE.test(valor)) {
    return null
  }

  // A referência para consultar o deslocamento: a mesma parede lida como UTC.
  // O erro possível é de uma hora, e só no instante exato da virada do horário
  // de verão — que hoje não existe no país.
  const referencia = new Date(`${valor}:00Z`)

  if (Number.isNaN(referencia.getTime())) {
    return null
  }

  return `${valor}:00${deslocamentoDaOperacao(referencia)}`
}

/** O caminho de volta: instante da API → o que o campo de hora local mostra. */
export function paraCampoDeInstante(instante: string | null | undefined): string {
  if (instante === null || instante === undefined || instante.length === 0) {
    return ''
  }

  const data = new Date(instante)

  if (Number.isNaN(data.getTime())) {
    return ''
  }

  return formatInTimeZone(data, "yyyy-MM-dd'T'HH:mm")
}

/**
 * A crítica do par retirada/devolução, para a tela dizer o que há de errado
 * ANTES de gastar uma requisição — e para não dispará-la enquanto a pessoa
 * ainda digita (ponta vazia devolve `null`, que é "não sei ainda", não "está
 * certo").
 *
 * As regras não são reescritas aqui: `periodoValidoSchema` é o mesmo que a API
 * aplica, com as mesmas mensagens em pt-BR. As três funções de PERÍODO da fase
 * moram juntas neste arquivo de propósito — o simulador e a tela de
 * disponibilidade perguntam a mesma coisa, e uma segunda cópia seria a que
 * aceitaria o intervalo que a outra recusa.
 */
export function erroDoPeriodo(retirada: string, devolucao: string): string | null {
  if (retirada.length === 0 || devolucao.length === 0) {
    return null
  }

  const validado = periodoValidoSchema.safeParse({ retirada, devolucao })

  if (validado.success) {
    return null
  }

  return validado.error.issues[0]?.message ?? 'período inválido'
}

/**
 * O dia local relativo a hoje, em `AAAA-MM-DD`.
 *
 * Passa por `paraCampoDeInstante` (que formata no fuso da operação) em vez de
 * `toISOString().slice(0, 10)`: das 21h em diante o dia UTC já é o seguinte, e a
 * tela abriria no dia errado justamente no turno da noite.
 */
export function diaRelativo(dias: number): string {
  const data = new Date(Date.now() + dias * UM_DIA_EM_MS)

  return paraCampoDeInstante(data.toISOString()).slice(0, 10)
}

/** O mesmo dia com a hora de parede colada — o valor sugerido de um período. */
export function campoDeInstanteRelativo(dias: number, hora: string): string {
  return `${diaRelativo(dias)}T${hora}`
}

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

/** O que está sendo ocupado. */
export type AlvoDoBloqueio = 'VEICULO' | 'PRESTADOR'

/**
 * Só os campos cujo nome BATE com o do schema entram aqui: `aplicarErrosDoZod`
 * casa o `path` da issue com o nome do campo, e `alvoId` (que é `vehicleId` ou
 * `serviceProviderId` do outro lado) cairia no alerta geral de qualquer forma.
 */
const CAMPOS = ['motivo', 'inicio', 'fim', 'observacoes'] as const

/** Só a frota de pé: bloquear um carro já baixado não ocupa nada. */
const FROTA_ATIVA: FiltrosDeVeiculos = {
  pagina: 1,
  tamanho: TAMANHO_MAXIMO_DE_PAGINA,
  busca: '',
  categoryId: '',
  locationId: '',
  status: '',
  blindagemVencendoEmDias: '',
  incluirBaixados: false,
}

/** Quem está de pé na escala. Inativo não recebe turno novo. */
const PRESTADORES_ATIVOS: FiltrosDePrestadores = {
  pagina: 1,
  tamanho: TAMANHO_MAXIMO_DE_PAGINA,
  tipo: '',
  status: 'ATIVO',
  locationId: '',
  livreDe: '',
  livreAte: '',
}

const MOTIVO_PADRAO: MotivoDeBloqueio = MOTIVOS_OPERACIONAIS[0] ?? 'MANUTENCAO'

interface FormularioDeBloqueio {
  /** `vehicleId` ou `serviceProviderId`, conforme o alvo. */
  alvoId: string
  motivo: MotivoDeBloqueio
  inicio: string
  fim: string
  observacoes: string
}

function valoresIniciais(
  bloqueio: BloqueioDeVeiculo | undefined,
  alvoIdInicial: string,
): FormularioDeBloqueio {
  return {
    alvoId: bloqueio?.vehicleId ?? alvoIdInicial,
    motivo: bloqueio?.motivo ?? MOTIVO_PADRAO,
    inicio: paraCampoDeInstante(bloqueio?.inicio),
    fim: paraCampoDeInstante(bloqueio?.fim),
    observacoes: bloqueio?.observacoes ?? '',
  }
}

// ---------------------------------------------------------------------------
// Seletor do alvo
// ---------------------------------------------------------------------------

/** Uma opção do seletor, com o texto já montado por quem sabe a forma do item. */
interface OpcaoDoAlvo {
  readonly id: string
  readonly rotulo: string
}

/** O que o formulário entrega ao seletor, seja ele de carro ou de gente. */
interface SeletorDoAlvoProps {
  readonly registro: UseFormRegisterReturn<'alvoId'>
  readonly erro: string | undefined
  readonly desabilitado: boolean
}

interface CampoDoAlvoProps extends SeletorDoAlvoProps {
  readonly rotulo: string
  readonly dica: string
  readonly placeholder: string
  readonly placeholderCarregando: string
  readonly tituloDoVazio: string
  readonly descricaoDoVazio: string
  readonly opcoes: readonly OpcaoDoAlvo[]
  readonly carregando: boolean
  readonly erroDaLista: unknown
  readonly aoTentarNovamente: () => void
}

/**
 * O seletor de quem vai ficar ocupado — com os TRÊS estados da consulta.
 *
 * Carregando é o texto da opção vazia; erro é o alerta com "tentar de novo"
 * (um seletor mudo pareceria diálogo travado); e VAZIO tem alerta próprio,
 * porque uma lista sem nenhuma opção é indistinguível de uma lista que falhou —
 * e o caminho de saída dela (cadastrar o carro, ativar o prestador) fica em
 * outra tela, que é justamente o que a frase precisa dizer.
 */
function CampoDoAlvo({
  rotulo,
  dica,
  placeholder,
  placeholderCarregando,
  tituloDoVazio,
  descricaoDoVazio,
  opcoes,
  carregando,
  erroDaLista,
  aoTentarNovamente,
  registro,
  erro,
  desabilitado,
}: CampoDoAlvoProps): ReactElement {
  const semOpcoes = !carregando && erroDaLista === undefined && opcoes.length === 0

  return (
    <>
      <Campo rotulo={rotulo} obrigatorio erro={erro} dica={dica}>
        {(campo) => (
          <Select {...campo} {...registro} disabled={desabilitado || carregando || semOpcoes}>
            <option value="">{carregando ? placeholderCarregando : placeholder}</option>
            {opcoes.map((opcao) => (
              <option key={opcao.id} value={opcao.id}>
                {opcao.rotulo}
              </option>
            ))}
          </Select>
        )}
      </Campo>

      {erroDaLista === undefined ? null : (
        <Alert variant="danger">
          <AlertTitle>Não foi possível carregar a lista</AlertTitle>
          <AlertDescription>{errorMessage(erroDaLista)}</AlertDescription>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={aoTentarNovamente}
          >
            <RefreshCw aria-hidden="true" />
            Tentar de novo
          </Button>
        </Alert>
      )}

      {semOpcoes ? (
        <Alert variant="warning">
          <AlertTitle>{tituloDoVazio}</AlertTitle>
          <AlertDescription>{descricaoDoVazio}</AlertDescription>
        </Alert>
      ) : null}
    </>
  )
}

/**
 * Um componente por alvo para que só a consulta do alvo ESCOLHIDO aconteça.
 *
 * Chamar os dois hooks no mesmo componente traria a frota inteira ao abrir a
 * escala e a escala inteira ao bloquear um carro — e, para quem tem
 * `availability.update` sem `service_provider.read`, um 403 por abertura de
 * diálogo.
 */
function SeletorDeVeiculo(props: SeletorDoAlvoProps): ReactElement {
  const veiculos = useVeiculos(FROTA_ATIVA)

  return (
    <CampoDoAlvo
      rotulo="Veículo"
      dica="O bloqueio é por placa; a disponibilidade que ele muda é a da categoria."
      placeholder="Selecione o veículo"
      placeholderCarregando="Carregando a frota…"
      tituloDoVazio="Nenhum veículo ativo na frota"
      descricaoDoVazio="Só carro de pé ocupa disponibilidade — o que já foi baixado não conta para categoria nenhuma. Cadastre ou reative um veículo na tela de frota."
      opcoes={(veiculos.data?.itens ?? []).map((veiculo) => ({
        id: veiculo.id,
        rotulo: `${identificacaoDoVeiculo(veiculo)} · ${veiculo.categoria.nome}`,
      }))}
      carregando={veiculos.isPending}
      erroDaLista={veiculos.isError ? veiculos.error : undefined}
      aoTentarNovamente={() => {
        void veiculos.refetch()
      }}
      {...props}
    />
  )
}

function SeletorDePrestador(props: SeletorDoAlvoProps): ReactElement {
  const prestadores = usePrestadores(PRESTADORES_ATIVOS)

  return (
    <CampoDoAlvo
      rotulo="Prestador"
      dica="Só quem está ativo aparece aqui."
      placeholder="Selecione o prestador"
      placeholderCarregando="Carregando…"
      tituloDoVazio="Nenhum prestador ativo"
      descricaoDoVazio="Sem motorista e sem segurança de pé não há escala a ocupar — e a cotação já recusa os extras que dependem deles. Cadastre ou reative alguém na tela de disponibilidade."
      opcoes={(prestadores.data?.itens ?? []).map((prestador) => ({
        id: prestador.id,
        rotulo: `${prestador.nome} · ${ROTULO_DO_TIPO_DE_PRESTADOR[prestador.tipo]}`,
      }))}
      carregando={prestadores.isPending}
      erroDaLista={prestadores.isError ? prestadores.error : undefined}
      aoTentarNovamente={() => {
        void prestadores.refetch()
      }}
      {...props}
    />
  )
}

// ---------------------------------------------------------------------------
// Diálogo
// ---------------------------------------------------------------------------

export interface DialogoDeBloqueioProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** `VEICULO` tira o carro de circulação; `PRESTADOR` ocupa a agenda da pessoa. */
  readonly alvo: AlvoDoBloqueio
  /** `undefined` cria; preenchido edita — e só bloqueio de VEÍCULO é editável. */
  readonly bloqueio?: BloqueioDeVeiculo | undefined
  /** Pré-seleciona o alvo quando a tela já sabe qual (ação de linha da tabela). */
  readonly alvoIdInicial?: string | undefined
}

interface CorpoDoBloqueioProps {
  readonly aoFechar: () => void
  readonly alvo: AlvoDoBloqueio
  readonly bloqueio: BloqueioDeVeiculo | undefined
  readonly alvoIdInicial: string
}

/**
 * O formulário. Vive num componente próprio porque o `DialogContent` do Radix
 * só monta os filhos com o diálogo ABERTO: assim a frota (ou a escala) é
 * consultada na abertura, e não em toda visita à tela de disponibilidade, onde
 * o diálogo fica montado esperando um clique que talvez não venha.
 *
 * A montagem por abertura também dispensa o efeito que ressincronizava o
 * formulário: `defaultValues` já é calculado com o bloqueio da vez.
 */
function CorpoDoBloqueio({
  aoFechar,
  alvo,
  bloqueio,
  alvoIdInicial,
}: CorpoDoBloqueioProps): ReactElement {
  const editando = bloqueio !== undefined
  const daReserva =
    bloqueio !== undefined && MOTIVOS_CONTROLADOS_PELA_RESERVA.includes(bloqueio.motivo)
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const criarDeVeiculo = useCriarBloqueio()
  const atualizarDeVeiculo = useAtualizarBloqueio()
  const criarDePrestador = useCriarBloqueioDePrestador()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormularioDeBloqueio>({
    defaultValues: valoresIniciais(bloqueio, alvoIdInicial),
  })

  const ocupado =
    criarDeVeiculo.isPending || atualizarDeVeiculo.isPending || criarDePrestador.isPending

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const inicio = paraInstante(valores.inicio)
    const fim = paraInstante(valores.fim)

    if (inicio === null) {
      setError('inicio', { type: 'validate', message: 'informe a data e a hora de início' })
      return
    }

    if (fim === null) {
      setError('fim', { type: 'validate', message: 'informe a data e a hora de término' })
      return
    }

    const observacoes = valores.observacoes.trim()

    if (editando) {
      // Na edição o campo vazio vira `null` (e não ausência): `undefined`
      // significaria "não mexa", e apagar a observação precisa APAGAR.
      const entrada = {
        motivo: valores.motivo,
        inicio,
        fim,
        observacoes: observacoes.length > 0 ? observacoes : null,
      }

      const validado = atualizarBloqueioDeVeiculoSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      // `entrada`, e não `validado.data`: o schema TRANSFORMA os instantes em
      // `Date`, e `JSON.stringify` de um `Date` já não é o ISO que a API espera
      // depois de passar por outro `JSON.parse`.
      atualizarDeVeiculo.mutate(
        { id: bloqueio.id, dados: entrada },
        {
          onSuccess: () => {
            toast.success('Bloqueio atualizado', {
              description: 'A disponibilidade do período foi recalculada.',
            })
            aoFechar()
          },
          onError: (causa: unknown) => {
            setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
          },
        },
      )
      return
    }

    const periodo = {
      motivo: valores.motivo,
      inicio,
      fim,
      ...(observacoes.length > 0 ? { observacoes } : {}),
    }

    if (alvo === 'PRESTADOR') {
      const entrada = { serviceProviderId: valores.alvoId, ...periodo }
      const validado = criarBloqueioDePrestadorSchema.safeParse(entrada)

      if (!validado.success) {
        setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
        return
      }

      criarDePrestador.mutate(entrada, {
        onSuccess: (criado) => {
          toast.success(`${criado.prestador.nome} está ocupado no período`, {
            description: 'A cotação deixa de oferecer o extra que depende desta pessoa.',
          })
          aoFechar()
        },
        onError: (causa: unknown) => {
          setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
        },
      })
      return
    }

    const entrada = { vehicleId: valores.alvoId, ...periodo }
    const validado = criarBloqueioDeVeiculoSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    criarDeVeiculo.mutate(entrada, {
      onSuccess: (criado) => {
        toast.success(`${identificacaoDoVeiculo(criado.veiculo)} bloqueado`, {
          description: 'A categoria perdeu uma unidade disponível neste período.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  const registroDoAlvo = register('alvoId', {
    required: alvo === 'VEICULO' ? 'selecione o veículo' : 'selecione o prestador',
  })

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {editando
            ? 'Editar bloqueio'
            : alvo === 'VEICULO'
              ? 'Bloquear veículo'
              : 'Ocupar prestador na escala'}
        </DialogTitle>
        <DialogDescription>
          {alvo === 'VEICULO'
            ? 'O carro sai da contagem de disponíveis da categoria durante o período.'
            : 'A pessoa fica indisponível no período, e a cotação para de oferecer o extra que depende dela.'}
        </DialogDescription>
      </DialogHeader>

      <form
        noValidate
        onSubmit={(evento) => {
          void aoEnviar(evento)
        }}
        className="space-y-4"
      >
        {erroGeral === null ? null : (
          <Alert variant="danger">
            <AlertDescription>{erroGeral}</AlertDescription>
          </Alert>
        )}

        {daReserva ? (
          <Alert variant="warning">
            <AlertTitle>Este bloqueio é da reserva</AlertTitle>
            <AlertDescription>
              Motivo {ROTULO_DO_MOTIVO_DE_BLOQUEIO[bloqueio.motivo]} é escrito pela reserva, não
              pela operação. Liberar o carro por aqui não cancelaria a reserva — só faria o sistema
              vender de novo um carro que já tem dono.
            </AlertDescription>
          </Alert>
        ) : null}

        {editando ? (
          <div className="rounded-lg border border-border bg-surface p-4">
            <p className="truncate font-medium text-foreground">
              {identificacaoDoVeiculo(bloqueio.veiculo)}
            </p>
            <p className="mt-1 text-xs text-fg-secondary">
              {bloqueio.veiculo.marca} · o veículo não é editável: para trocar de carro, libere este
              bloqueio e crie outro.
            </p>
          </div>
        ) : alvo === 'VEICULO' ? (
          <SeletorDeVeiculo
            registro={registroDoAlvo}
            erro={errors.alvoId?.message}
            desabilitado={ocupado}
          />
        ) : (
          <SeletorDePrestador
            registro={registroDoAlvo}
            erro={errors.alvoId?.message}
            desabilitado={ocupado}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            rotulo="Motivo"
            obrigatorio
            erro={errors.motivo?.message}
            className="sm:col-span-2"
          >
            {(campo) => (
              <Select {...campo} {...register('motivo')} disabled={ocupado || daReserva}>
                {MOTIVOS_OPERACIONAIS.map((motivo) => (
                  <option key={motivo} value={motivo}>
                    {ROTULO_DO_MOTIVO_DE_BLOQUEIO[motivo]}
                  </option>
                ))}
              </Select>
            )}
          </Campo>

          <Campo
            rotulo="Início"
            obrigatorio
            erro={errors.inicio?.message}
            dica="Hora de São Paulo."
          >
            {(campo) => (
              <Input
                {...campo}
                {...register('inicio', { required: 'informe o início' })}
                type="datetime-local"
                className="tabular-money"
                disabled={ocupado || daReserva}
              />
            )}
          </Campo>

          <Campo rotulo="Término" obrigatorio erro={errors.fim?.message} dica="Hora de São Paulo.">
            {(campo) => (
              <Input
                {...campo}
                {...register('fim', { required: 'informe o término' })}
                type="datetime-local"
                className="tabular-money"
                disabled={ocupado || daReserva}
              />
            )}
          </Campo>

          <Campo
            rotulo="Observações"
            erro={errors.observacoes?.message}
            className="sm:col-span-2"
            dica="O que a operação precisa saber — oficina, número da OS, previsão."
          >
            {(campo) => (
              <Textarea
                {...campo}
                {...register('observacoes')}
                rows={3}
                disabled={ocupado || daReserva}
              />
            )}
          </Campo>
        </div>

        <Alert variant="info">
          <AlertTitle>O fim do período é aberto</AlertTitle>
          <AlertDescription>
            Um bloqueio que termina às 10h e outro que começa às 10h não se sobrepõem. Dois períodos
            que de fato se cruzam no mesmo alvo são recusados pelo banco, e não pela tela — a
            garantia é do Postgres.
          </AlertDescription>
        </Alert>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
            {daReserva ? 'Fechar' : 'Cancelar'}
          </Button>

          {daReserva ? null : (
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              {editando ? 'Salvar alterações' : 'Bloquear'}
            </Button>
          )}
        </DialogFooter>
      </form>
    </>
  )
}

export function DialogoDeBloqueio({
  aberto,
  aoFechar,
  alvo,
  bloqueio,
  alvoIdInicial = '',
}: DialogoDeBloqueioProps): ReactElement {
  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <CorpoDoBloqueio
          aoFechar={aoFechar}
          alvo={alvo}
          bloqueio={bloqueio}
          alvoIdInicial={alvoIdInicial}
        />
      </DialogContent>
    </Dialog>
  )
}
